import {
  appendFirestoreDiagnosticsHeartbeat,
  clearFirestoreDiagnostics,
  getFirestoreDiagnosticsEnabled,
  getFirestoreDiagnosticsForced,
  getFirestoreDiagnosticsHeartbeats,
  setFirestoreDiagnosticsEnabled,
} from 'shared/services/local-storage/firestore-diagnostics';

export enum DiagnosticsChannel {
  Heartbeat = 'heartbeat',
  Snapshot = 'snapshot',
  FirstSnapshot = 'first-snapshot',
  Listener = 'listener',
  LongTask = 'long-task',
  Write = 'write',
}

export type DiagnosticsEntry = {
  channel: DiagnosticsChannel;
  atMs: number;
  uptimeMs: number;
  data: Record<string, unknown>;
};

const maxRingEntries = 2000;

const sessionStartMs = Date.now();

// Resolved ONCE at module init. The hot path reads this boolean — never
// localStorage, which is a synchronous storage call and would be a real tax at
// per-snapshot frequency. `refreshDiagnosticsEnabled` re-reads on session change.
let enabled =
  getFirestoreDiagnosticsEnabled() || getFirestoreDiagnosticsForced();

const ring: DiagnosticsEntry[] = [];
let dropped = 0;

export const isDiagnosticsEnabled = () => enabled;

// Called when the session user resolves, so a flag written last session is
// already active during boot — that is what makes boot-time subscriptions
// observable at all.
export const refreshDiagnosticsEnabled = (shouldEnable: boolean) => {
  setFirestoreDiagnosticsEnabled(shouldEnable);
  enabled = shouldEnable || getFirestoreDiagnosticsForced();
};

const push = (entry: DiagnosticsEntry) => {
  ring.push(entry);
  if (ring.length > maxRingEntries) {
    ring.shift();
    dropped += 1;
  }
};

/**
 * `build` is a thunk on purpose: when diagnostics are off nothing is evaluated,
 * so an expensive payload (JSON.stringify of a query key, a doc count) costs
 * nothing on the path being measured.
 */
export const logDiagnostic = (
  channel: DiagnosticsChannel,
  build: () => Record<string, unknown>,
) => {
  if (!enabled) {
    return;
  }

  const now = Date.now();
  const entry: DiagnosticsEntry = {
    channel,
    atMs: now,
    uptimeMs: now - sessionStartMs,
    data: build(),
  };

  push(entry);

  if (channel === DiagnosticsChannel.Heartbeat) {
    appendFirestoreDiagnosticsHeartbeat(entry);
  }

  // eslint-disable-next-line no-console -- the single console site for diagnostics; gated above
  console.log(`[fs-diag:${channel}]`, entry.data);
};

export const getDiagnosticsReport = () => ({
  sessionStartMs,
  collectedAtMs: Date.now(),
  uptimeMs: Date.now() - sessionStartMs,
  droppedEntries: dropped,
  // Persisted heartbeats first: they span previous sessions, the ring does not.
  persistedHeartbeats: getFirestoreDiagnosticsHeartbeats(),
  entries: [...ring],
});

export const copyDiagnosticsReport = async (): Promise<boolean> => {
  try {
    await window.navigator.clipboard.writeText(
      JSON.stringify(getDiagnosticsReport(), null, 2),
    );
    return true;
  } catch {
    return false;
  }
};

// The practical "send it over" path: a real file the user can drag into Slack.
// A week of ring + heartbeats is far too large to paste comfortably.
export const downloadDiagnosticsReport = (): string => {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  const fileName = `griply-firestore-diagnostics-${stamp}.json`;
  const blob = new Blob([JSON.stringify(getDiagnosticsReport(), null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);

  return fileName;
};

type HeartbeatSample = {
  uptimeMs?: number;
  data?: Record<string, unknown>;
};

const readMetric = (sample: HeartbeatSample | undefined, key: string) =>
  typeof sample?.data?.[key] === 'number'
    ? (sample.data[key] as number)
    : undefined;

const growth = (
  first: HeartbeatSample | undefined,
  last: HeartbeatSample | undefined,
  key: string,
) => {
  const from = readMetric(first, key);
  const to = readMetric(last, key);
  if (from === undefined || to === undefined) {
    return undefined;
  }
  return { from, to, delta: to - from };
};

// First-vs-last deltas across the persisted series — the one-glance answer to
// "what is growing?" without reading thousands of raw entries.
export const getDiagnosticsSummary = () => {
  const samples = getFirestoreDiagnosticsHeartbeats() as HeartbeatSample[];
  const first = samples[0];
  const last = samples.at(-1);

  return {
    samples: samples.length,
    spanHours:
      first?.uptimeMs !== undefined && last?.uptimeMs !== undefined
        ? Number(((last.uptimeMs - first.uptimeMs) / 3_600_000).toFixed(1))
        : undefined,
    liveListeners: growth(first, last, 'liveListeners'),
    totalDocsHeld: growth(first, last, 'totalDocsHeld'),
    usedJSHeapBytes: growth(first, last, 'usedJSHeapBytes'),
    storageUsageBytes: growth(first, last, 'storageUsageBytes'),
  };
};

export const resetDiagnostics = () => {
  ring.length = 0;
  dropped = 0;
  clearFirestoreDiagnostics();
};

type DiagnosticsConsoleApi = {
  summary: typeof getDiagnosticsSummary;
  report: typeof getDiagnosticsReport;
  download: typeof downloadDiagnosticsReport;
  copy: typeof copyDiagnosticsReport;
  reset: typeof resetDiagnostics;
};

// Devtools handle. Internal users already run with devtools open, so this is the
// whole retrieval UI — no product surface, nothing shipped to normal users.
export const registerDiagnosticsConsoleApi = () => {
  if (!enabled) {
    return;
  }
  (
    window as Window & { griplyDiagnostics?: DiagnosticsConsoleApi }
  ).griplyDiagnostics = {
    summary: getDiagnosticsSummary,
    report: getDiagnosticsReport,
    download: downloadDiagnosticsReport,
    copy: copyDiagnosticsReport,
    reset: resetDiagnostics,
  };
};
